
/**
 * The injectionContainer is a lightweight method of dependency injection, which
 * we are using to avoid cyclic file dependencies, particularly those where we
 * widely depend on an interface but don't need to depend on particular
 * implementations.
 *
 * It is intended to be used in the following way:
 *
 * - Instantiate a container adjacent to where the interface is defined
 * - Define a script that populates the containers, stored adjacent to where the
 *     implementations are defined
 *   - For static implementations, use the first parameter of the set function
 *   - For dynamic implementations (e.g. relying on feature flag values that get
 *     overriden at runtime), use the second parameter of the set function
 * 
 * - Import the population script when the app loads, from the top of
 *     `vizportal.ts` along with the other DI population scripts.
 */
export function injectionContainer<T>(thingName: string): { get: () => T, set: (t: T, tOverrideAtRuntime?: () => T) => void } {
  let thing: T;
  let thingOverrideAtRuntime: () => T;

  function set(t: T, tOverrideAtRuntime?: () => T): void {
    if (thing || thingOverrideAtRuntime) {
      throw new Error(`Cannot overwrite ${thingName}; it has already been initialized.`);
    }
    thing = t;
    thingOverrideAtRuntime = tOverrideAtRuntime;
  }

  function get(): T {
    if (!thing && !thingOverrideAtRuntime) {
      throw new Error(`Cannot access ${thingName}; it has not yet been initialized.`);
    }
    if (thingOverrideAtRuntime) {
      return thingOverrideAtRuntime();
    }
    return thing;
  }

  return { get, set };
}
